import React from 'react';
import DOMPurify from 'dompurify';
import WarningIcon from '@mui/icons-material/Warning';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForward from '@mui/icons-material/ArrowForward';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import NorthEast from '@mui/icons-material/NorthEast';
import SouthEast from '@mui/icons-material/SouthEast';
import SouthWest from '@mui/icons-material/SouthWest';
import NorthWest from '@mui/icons-material/NorthWest';

const Advisories = ({detailbarContent, HomeStyles, nearbyAirspaces = [], nearbyAirports = []}) => {
    // Convert airport type_code to readable name
    const getAirportType = (typeCode) => {
        const typeMap = {
            'AP': 'Airport',
            'AD': 'Airport',
            'SP': 'Sea Port',
            'HP': 'Heliport',
            'AL': 'Airfield'
        };
        return typeMap[typeCode] || typeCode;
    };

    // Get directional arrow icon component
    const getDirectionIcon = (direction) => {
        const iconProps = { style: { fontSize: '18px', verticalAlign: 'middle' } };
        const iconMap = {
            'E': <ArrowForward {...iconProps} />,
            'W': <ArrowBack {...iconProps} />,
            'N': <ArrowUpward {...iconProps} />,
            'S': <ArrowDownward {...iconProps} />,
            'NE': <NorthEast {...iconProps} />,
            'SE': <SouthEast {...iconProps} />,
            'SW': <SouthWest {...iconProps} />,
            'NW': <NorthWest {...iconProps} />
        };
        return iconMap[direction] || <ArrowForward {...iconProps} />;
    };
    
    // Function to get display name with prefix based on status
    const getDisplayNameWithPrefix = (displayName, status) => {
        // If status is NOK or DNF, add WARNING prefix
        if (status === 'NOK' || status === 'DNF') {
            return `WARNING: ${displayName}`;
        }
        // If status is ALERT or CAUTION, add CAUTION prefix
        else if (status === 'ALERT' || status === 'CAUTION') {
            return `CAUTION: ${displayName}`;
        }
        // If it's Uncontrolled Airspace, show CLEAR TO FLY
        else if (displayName === 'Uncontrolled Airspace') {
            return 'CLEAR TO FLY';
        }
        // Default - return original
        return displayName;
    };

    return (
    <div className={HomeStyles.advisories_container}>
        <h2 className={HomeStyles.advisories}>ADVISORIES</h2>
        <div className={detailbarContent.layers.summary && (
            detailbarContent.layers.summary.status === "NOK"
                ? HomeStyles.mapdata_mapdata01_NOK
                : detailbarContent.layers.summary.status === "DNF"
                    ? HomeStyles.mapdata_mapdata01_NOK
                    : detailbarContent.layers.summary.status === "ALERT"
                        ? HomeStyles.mapdata_mapdata01_ALERT
                        : HomeStyles.mapdata_mapdata01_OK
        )}>
            {detailbarContent.layers.summary && (
                <div
                    className={
                        detailbarContent.layers.summary.status === "NOK"
                            ? HomeStyles.mapdata_summary_NOK
                            : detailbarContent.layers.summary.status === "DNF"
                                ? HomeStyles.mapdata_summary_NOK
                                : detailbarContent.layers.summary.status === "ALERT"
                                    ? HomeStyles.mapdata_summary_ALERT
                                    : HomeStyles.mapdata_summary_OK
                    }
                >
                    <p className={HomeStyles.title_class}>{detailbarContent.layers.summary.title}</p>
                    {detailbarContent.layers.summary.subtitle &&
                        <p className={HomeStyles.sub_title_class}>{detailbarContent.layers.summary.subtitle}</p>}
                </div>
            )}
            {detailbarContent.layers.layers && detailbarContent.layers.layers.map((data_item, index) => {
                // USE displayName field from data_item
                const nameToShow = data_item.displayName || data_item.layerId;
                const displayName = getDisplayNameWithPrefix(nameToShow, data_item.status);
                
                return (
                    <div className={HomeStyles.mapdata02} key={index} style={{flexDirection: 'row', gap: '12px', alignItems: 'flex-start'}}>
                        <div style={{flexShrink: 0}}>
                            {(data_item.status === 'DNF' || data_item.status === 'NOK') && (
                                <WarningIcon style={{color: '#dc2626', fontSize: '20px', display: 'block'}} />
                            )}
                            {(data_item.status === 'ALERT' || data_item.status === 'CAUTION') && (
                                <WarningAmberIcon style={{color: '#f59e0b', fontSize: '20px', display: 'block'}} />
                            )}
                            {data_item.layerId === 'Uncontrolled Airspace' && (
                                <CheckCircleIcon style={{color: '#16a34a', fontSize: '20px', display: 'block'}} />
                            )}
                            {(data_item.status === 'OK' && data_item.layerId !== 'Uncontrolled Airspace') && (
                                <InfoIcon style={{color: '#3b82f6', fontSize: '20px', display: 'block'}} />
                            )}
                        </div>
                        <div style={{flex: 1, display: 'flex', flexDirection: 'column', gap: '2px'}}>
                            <p className={HomeStyles.title_class} style={{margin: 0, display: 'block'}}>
                                {displayName}
                            </p>
                            <p className={HomeStyles.description_class}
                               dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(data_item.description)}}/>
                            {data_item.flyzone_notice &&
                                <p className={HomeStyles.flyzone_notice_class}
                                   dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(data_item.flyzone_notice)}}/>}
                            {data_item.distanceNM && (
                                <p style={{margin: '4px 0 0 0', fontSize: '11px', color: '#6b7280', fontStyle: 'italic'}}>
                                    {data_item.distanceNM} NM away
                                </p>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>

        {/* Proximity Layers (2NM) - Non-Intersecting */}
        {detailbarContent.layers.proximityLayers && detailbarContent.layers.proximityLayers.length > 0 && (
            <div className={HomeStyles.mapdata_mapdata01_OK} style={{marginTop: '16px'}}>
                <div className={HomeStyles.mapdata_summary_OK}>
                    <p className={HomeStyles.title_class}>Nearby Restricted Airspace (within 2 NM)</p>
                    <p className={HomeStyles.sub_title_class} style={{fontSize: '11px', marginTop: '4px'}}>
                        These airspaces are within 2 nautical miles but do not intersect your operation area
                    </p>
                </div>
                {detailbarContent.layers.proximityLayers.map((data_item, index) => {
                    const nameToShow = data_item.displayName || data_item.layerId;
                    const displayName = getDisplayNameWithPrefix(nameToShow, data_item.status);

                    return (
                        <div className={HomeStyles.mapdata02} key={index} style={{flexDirection: 'row', gap: '12px', alignItems: 'flex-start'}}>
                            <div style={{flexShrink: 0}}>
                                {(data_item.status === 'DNF' || data_item.status === 'NOK') && (
                                    <WarningIcon style={{color: '#dc2626', fontSize: '20px', display: 'block'}} />
                                )}
                                {(data_item.status === 'ALERT' || data_item.status === 'CAUTION') && (
                                    <WarningAmberIcon style={{color: '#f59e0b', fontSize: '20px', display: 'block'}} />
                                )}
                                {data_item.layerId === 'Uncontrolled Airspace' && (
                                    <CheckCircleIcon style={{color: '#16a34a', fontSize: '20px', display: 'block'}} />
                                )}
                                {(data_item.status === 'OK' && data_item.layerId !== 'Uncontrolled Airspace') && (
                                    <InfoIcon style={{color: '#3b82f6', fontSize: '20px', display: 'block'}} />
                                )}
                            </div>
                            <div style={{flex: 1, display: 'flex', flexDirection: 'column', gap: '2px'}}>
                                <p className={HomeStyles.title_class} style={{margin: 0, display: 'block'}}>
                                    {displayName}
                                </p>
                                <p className={HomeStyles.description_class}
                                   dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(data_item.description)}}/>
                                {data_item.flyzone_notice &&
                                    <p className={HomeStyles.flyzone_notice_class}
                                       dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(data_item.flyzone_notice)}}/>}
                                {data_item.distanceNM && (
                                    <p style={{margin: '4px 0 0 0', fontSize: '11px', color: '#6b7280', fontStyle: 'italic', fontWeight: '600'}}>
                                        {data_item.distanceNM} NM away
                                    </p>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        )}

        {/* Operational Awareness Card */}
        {(nearbyAirspaces.length > 0 || nearbyAirports.length > 0) && (
            <div className={HomeStyles.mapdata_mapdata01_OK} style={{marginTop: '16px'}}>
                <div className={HomeStyles.mapdata_summary_OK}>
                    <p className={HomeStyles.title_class}>Operational Awareness</p>
                </div>

                {/* Class Airspaces Section */}
                {nearbyAirspaces.length > 0 && (
                    <div style={{padding: '8px 12px', borderBottom: nearbyAirports.length > 0 ? '1px solid #e5e7eb' : 'none'}}>
                        <p className={HomeStyles.sub_title_class} style={{margin: '0 0 6px 0', fontSize: '12px', fontWeight: '600', color: '#4b5563'}}>
                            Class Airspaces
                        </p>
                        {nearbyAirspaces.map((airspace, index) => {
                            const distanceInMiles = (parseFloat(airspace.distance) * 1.15078).toFixed(2);

                            return (
                                <div key={index} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', fontSize: '12px'}}>
                                    <span style={{flex: 1, marginRight: '8px'}}>
                                        {airspace.properties.NAME || 'Unnamed'} <span style={{color: '#6b7280'}}>({airspace.properties.CLASS || 'N/A'})</span>
                                    </span>
                                    <span style={{fontWeight: '500', whiteSpace: 'nowrap'}}>
                                        {airspace.isInside ? (
                                            <span style={{fontStyle: 'italic', color: '#059669'}}>📍 Pin inside</span>
                                        ) : (
                                            <span>{distanceInMiles} mi {getDirectionIcon(airspace.direction)}</span>
                                        )}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Airports Section */}
                {nearbyAirports.length > 0 && (
                    <div style={{padding: '8px 12px'}}>
                        <p className={HomeStyles.sub_title_class} style={{margin: '0 0 6px 0', fontSize: '12px', fontWeight: '600', color: '#4b5563'}}>
                            Airports
                        </p>
                        {nearbyAirports.map((airport, index) => {
                            const distanceInMiles = (parseFloat(airport.distance) * 1.15078).toFixed(2);

                            return (
                                <div key={index} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', fontSize: '12px'}}>
                                    <span style={{flex: 1, marginRight: '8px'}}>
                                        {airport.NAME || 'Unnamed'} <span style={{color: '#6b7280'}}>({getAirportType(airport.TYPE_CODE)})</span>
                                    </span>
                                    <span style={{fontWeight: '500', whiteSpace: 'nowrap'}}>
                                        {distanceInMiles} mi {getDirectionIcon(airport.direction)}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        )}
    </div>
    );
};

export default Advisories;